import { useState, useEffect } from 'react';
import { ApiFout, api } from '@/lib/api';
import type { BeheerPraktijk } from '@/lib/api-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, Save, Monitor } from 'lucide-react';
import { toast } from 'sonner';

/**
 * De vensterafmetingen per praktijk.
 *
 * Een deelverzameling van BeheerPraktijk: dit scherm gaat alleen over de grootte van
 * het opnamevenster, en de rest van de praktijkgegevens hoort er niet in.
 */
interface DisplaySettings {
  id: string;
  practiceName: string;
  recordWindowWidth: number;
  recordWindowHeight: number;
}

export function DisplaySettingsTab() {
  const [practices, setPractices] = useState<DisplaySettings[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { practices } = await api.get<{ practices: BeheerPraktijk[] }>('/admin/practices');
        setPractices(
          practices.map((p) => ({
            id: p.id,
            practiceName: p.practiceName,
            recordWindowWidth: p.recordWindowWidth,
            recordWindowHeight: p.recordWindowHeight,
          })),
        );
      } catch (fout) {
        toast.error(fout instanceof ApiFout ? fout.message : 'Kon de praktijken niet laden');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleSave = async (p: DisplaySettings) => {
    // De server valideert dit ook (200 tot 4000 px in praktijkUpdateSchema); deze
    // controle is er om de gebruiker een begrijpelijke melding te geven in plaats van
    // een veldfout uit zod.
    if (p.recordWindowWidth < 200 || p.recordWindowHeight < 200) {
      toast.error('Minimale afmeting is 200px');
      return;
    }

    setSavingId(p.id);
    try {
      await api.patch(`/admin/practices/${p.id}`, {
        recordWindowWidth: p.recordWindowWidth,
        recordWindowHeight: p.recordWindowHeight,
      });
      toast.success('Weergave opgeslagen');
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Opslaan mislukt');
    } finally {
      setSavingId(null);
    }
  };

  const update = (id: string, field: keyof DisplaySettings, value: number) => {
    setPractices(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <Monitor className="h-5 w-5" />
          Weergave
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {practices.map((p) => (
          <div key={p.id} className="border rounded-lg p-5 space-y-4">
            <p className="text-sm font-medium">{p.practiceName}</p>
            <div className="flex flex-wrap items-end gap-6">
              <div className="space-y-1.5">
                <Label className="text-sm">Breedte (px)</Label>
                <Input
                  type="number"
                  min={200}
                  step={10}
                  value={p.recordWindowWidth}
                  onChange={(e) => update(p.id, 'recordWindowWidth', parseInt(e.target.value) || 200)}
                  className="w-[140px]"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Hoogte (px)</Label>
                <Input
                  type="number"
                  min={200}
                  step={10}
                  value={p.recordWindowHeight}
                  onChange={(e) => update(p.id, 'recordWindowHeight', parseInt(e.target.value) || 200)}
                  className="w-[140px]"
                />
              </div>
              <Button onClick={() => handleSave(p)} disabled={savingId === p.id} size="sm">
                {savingId === p.id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Opslaan
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Voorvertoning: {p.recordWindowWidth} × {p.recordWindowHeight} px
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
